import React, { Fragment, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import { Elements as StripeElements } from '@stripe/react-stripe-js';
import { Prompt } from 'react-router-dom';

import useProRegister from './hooks/useProRegister';
import useStripePromise from '../../../hooks/useStripePromise';
import RegisterViewDetailsForm from './components/RegisterViewDetailsForm';
import RegisterCheckoutCart from './components/RegisterCheckoutCart';
import { ACCOUNT_REFERRAL_TYPE } from '../../../constants/account-referrals';
import { isValidEmail } from '../../../util/email';
import { PRO_REGISTRATION_STEP } from './helpers';
import Logo from '../../../components/global/Logo';
import RegisterPhotoManagerForm from './components/RegisterPhotoManagerForm';
import Spinner from '../../../components/global/Spinner';
import EventSKUSelection from './components/EventSKUSelection';
import Button from '../../../components/global/Button';
import Angle from '../../../components/global/Angle';
import RegistrationFundraiserMarketingCommitment from './components/RegistrationFundraiserMarketingCommitment';
import PLICBooksLogin from '../../../components/global/PLICBooksLogin';
import RegisterPhotoFinderForm from './components/RegisterPhotoFinderForm';
import MarcomLayout from '../../../components/global/MarcomLayout';
import RegisterViewPinVerificationForm from './components/RegisterViewPinVerificationForm';
import RegisterViewPasswordCreateForm from './components/RegisterViewPasswordCreateForm';
import { isValidPhone } from '../../../util/phone';
import { SESSION_STATE } from '../../../apps/shutterbug/views/SignupAlbumCreate/helpers';

const RegisterView = () => {
  const stripePromise = useStripePromise();
  const [reminderSet, setReminderSet] = useState(false);
  const {
    loading,
    onAccountPromoCodeVerify,
    photoManagerSubscriptionItems,
    defaultPersona,
    photoFinderCreditItems = [],
    defaultPhotoFinderTier,
    defaultPhotoManagerItem,
    activeStep,
    onSubmit,
    loadingSubscriptionItems,
    onAccountUsernameVerify,
    onAccountEmailVerify,
    onAccountPhoneNumberVerify,
    accountPromoCode,
    canGoBack,
    onGoBack,
    defaultPromoCode,
    personaDisabled,
    noMarketing,
    skipFreeTrial,
    skipPhotoFinder,
    isWaldoProFreeRegister,
    verifyingDefaultPromoCode,
    onEmailPinCodeRequest,
    onPhotoManagerPurchaseReminderSet,
    setStartForFreeButtonClicked,
    startForFreeButtonClicked,
  } = useProRegister();

  useEffect(() => {
    if (activeStep === PRO_REGISTRATION_STEP.CART && !reminderSet) {
      onPhotoManagerPurchaseReminderSet({
        session_state: SESSION_STATE.START,
      });
      setReminderSet(true);
    }
  }, [activeStep]);

  const onValidate = async values => {
    const errors = {};

    switch (activeStep) {
      case PRO_REGISTRATION_STEP.PHOTO_MANAGER_SUBSCRIPTION_SELECTION: {
        if (values.promo_code.length > 0) {
          const enteredPromoCode = await onAccountPromoCodeVerify(
            values.promo_code,
          );

          if (
            !enteredPromoCode.is_valid ||
            ![
              ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE,
              ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY,
              ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY,
              ACCOUNT_REFERRAL_TYPE.PHOTO_FINDER_YEARLY,
              ACCOUNT_REFERRAL_TYPE.PHOTO_FINDER_MONTHLY,
            ].includes(enteredPromoCode.type)
          ) {
            errors.promo_code =
              'The promo code you provided does not exist or is no longer active.';
          }
        }

        break;
      }
      case PRO_REGISTRATION_STEP.FREE_TRIAL:
      case PRO_REGISTRATION_STEP.DETAILS: {
        const { exists } = await onAccountUsernameVerify(values.username);
        const { exists: emailExists } = await onAccountEmailVerify(
          values.email_address,
        );
        const { exists: phoneExists } = await onAccountPhoneNumberVerify(
          values.contact_phone,
        );

        if (
          !values.organization_name ||
          values.organization_name.trim() === ''
        ) {
          errors.organization_name = 'Company name is required';
        }

        if (!isValidEmail(values.email_address)) {
          errors.email_address = 'This email address is not valid';
        }

        if (!isValidPhone(values.contact_phone)) {
          errors.contact_phone = 'This phone number is not valid';
        }

        if (exists) {
          errors.username = 'This username is already taken';
        }

        // Covers GQL being unable to parse
        if (typeof emailExists === 'undefined') {
          errors.email_address = 'This email address is not valid';
        }

        if (emailExists) {
          errors.email_address = 'This email address is already taken';
        }

        // Covers GQL being unable to parse
        if (typeof phoneExists === 'undefined') {
          errors.contact_phone = 'This phone number is not valid';
        }

        break;
      }
      case PRO_REGISTRATION_STEP.PIN_VERIFICATION: {
        if (values.pin_code.length !== 4) {
          errors.pin_code = 'Pin code must be 4 digits in length';
        }

        if (Number.isNaN(values.pin_code)) {
          errors.pin_code = 'Pin code must be a number';
        }

        break;
      }

      case PRO_REGISTRATION_STEP.PASSWORD_CREATION: {
        if (values.password.length <= 7) {
          errors.password = 'Password must be at least 8 characters long';
        }

        if (values.password !== values.password_confirm) {
          errors.password_confirm = 'Passwords must match';
        }

        break;
      }
      case PRO_REGISTRATION_STEP.FUNDRAISER_MARKETING_COMMITMENT: {
        if (
          !values.email_per_month ||
          !values.sms_per_month ||
          !values.social_post_per_month ||
          !values.banner_on_website ||
          !values.addon_for_online_purchases
        ) {
          errors.email_per_month = 'You must agree to all items.';
        }

        break;
      }
      default:
        break;
    }

    return errors;
  };

  if (loadingSubscriptionItems || verifyingDefaultPromoCode) {
    return (
      <div className="content-wrap-escape">
        <div className="container">
          <div className="site-brand">
            <Logo />
          </div>

          <div className="d-flex p-5">
            <Spinner className="m-auto" />
          </div>
        </div>
      </div>
    );
  }

  const title = (() => {
    if (
      [
        PRO_REGISTRATION_STEP.FREE_TRIAL,
        PRO_REGISTRATION_STEP.DETAILS,
        PRO_REGISTRATION_STEP.PHOTO_FINDER_CREDITS_SELECTION,
      ].includes(activeStep)
    ) {
      return <>Create your account below</>;
    }

    if (isWaldoProFreeRegister) {
      return <>Get started with WaldoPro!</>;
    }

    if (activeStep === PRO_REGISTRATION_STEP.PIN_VERIFICATION) {
      return <>Verify your email address</>;
    }

    if (activeStep === PRO_REGISTRATION_STEP.PASSWORD_CREATION) {
      return <>Create a password</>;
    }

    if (activeStep === PRO_REGISTRATION_STEP.CART) {
      return <>Your payment details</>;
    }

    if (activeStep === PRO_REGISTRATION_STEP.FUNDRAISER_MARKETING_COMMITMENT) {
      return <>Waldo School Fundraiser Marketing Commitment</>;
    }

    if (activeStep === PRO_REGISTRATION_STEP.PLIC_LOGIN) {
      return <>Sign up with PLIC Books</>;
    }

    if (activeStep === PRO_REGISTRATION_STEP.EVENT_SKU_SELECTION) {
      return <>Waldo-fy your next event!</>;
    }

    return <>Get started with Waldo!</>;
  })();

  return (
    <StripeElements stripe={stripePromise}>
      <Helmet>
        <title>Register &ndash; Waldo</title>
      </Helmet>
      <MarcomLayout.Wrapper
        signupDisabled
        title={title}
        isProView={isWaldoProFreeRegister}
        contentClassName={
          activeStep === PRO_REGISTRATION_STEP.EVENT_SKU_SELECTION
            ? 'marcom-layout-content--event-pricing'
            : ''
        }
      >
        <div className="register-view-wrapper">
          {canGoBack && (
            <Button
              onClick={onGoBack}
              className="text-extra-bold mb-3"
              btnStyle="link"
            >
              <Angle direction="left" className="mr-1" /> Back
            </Button>
          )}
          <div className="photo-manager-registration-form-wrapper">
            <Formik
              // Forces a re-render if the default photo manager
              // is updated through the URL somehow. At the moment,
              // Only possible via a URL promo code.
              key={defaultPhotoManagerItem?.id}
              initialValues={{
                first_name: '',
                last_name: '',
                organization_name: '',
                email_address: '',
                contact_phone: '+1',
                pin_code: '',
                password: '',
                password_confirm: '',
                username: '',
                persona: defaultPersona ?? '',
                promo_code: '',
                default_promo_code: defaultPromoCode ?? '',

                // Checkout logic
                stripe_token: null,
                requires_payment: false,
                plan_id: defaultPhotoManagerItem?.id ?? null,
                tier_id: defaultPhotoFinderTier?.id ?? null,

                // Event addon items:
                event_addon_item_id: null,

                // fundraiser marketing mateiral commitment:
                email_per_month: false,
                sms_per_month: false,
                social_post_per_month: false,
                banner_on_website: false,
                addon_for_online_purchases: true,
              }}
              validate={onValidate}
              onSubmit={onSubmit}
              validateOnBlur={false}
              validateOnChange={false}
              render={({
                values,
                handleSubmit,
                handleChange,
                handleBlur,
                errors,
                touched = {},
                isValid,
                validateForm,
              }) => {
                const selectedSubscriptionItem =
                  photoManagerSubscriptionItems?.find(
                    item => item.id === values.plan_id,
                  );

                return (
                  <Fragment>
                    {activeStep ===
                      PRO_REGISTRATION_STEP.EVENT_SKU_SELECTION && (
                      <EventSKUSelection
                        handleChange={handleChange}
                        handleSubmit={handleSubmit}
                      />
                    )}
                    {activeStep ===
                      PRO_REGISTRATION_STEP.PHOTO_MANAGER_SUBSCRIPTION_SELECTION && (
                      <RegisterPhotoManagerForm
                        values={values}
                        photoManagerSubscriptionItems={
                          photoManagerSubscriptionItems
                        }
                        handleSubmit={handleSubmit}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        errors={errors}
                        touched={touched}
                        accountPromoCode={accountPromoCode}
                        skipFreeTrial={skipFreeTrial}
                        isWaldoProFreeRegister={isWaldoProFreeRegister}
                        setStartForFreeButtonClicked={
                          setStartForFreeButtonClicked
                        }
                      />
                    )}
                    {activeStep ===
                      PRO_REGISTRATION_STEP.PHOTO_FINDER_CREDITS_SELECTION && (
                      <RegisterPhotoFinderForm
                        forceOpen
                        values={values}
                        handleSubmit={handleSubmit}
                        handleChange={handleChange}
                        photoFinderCreditItems={photoFinderCreditItems}
                      />
                    )}
                    {[
                      PRO_REGISTRATION_STEP.FREE_TRIAL,
                      PRO_REGISTRATION_STEP.DETAILS,
                      PRO_REGISTRATION_STEP.PHOTO_FINDER_CREDITS_SELECTION,
                    ].includes(activeStep) && (
                      <RegisterViewDetailsForm
                        values={values}
                        handleSubmit={handleSubmit}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        errors={errors}
                        touched={touched}
                        loading={loading}
                        requiresCreditCard={
                          values.promo_code?.length > 0 ||
                          !!selectedSubscriptionItem
                        }
                        personaDisabled={personaDisabled}
                        isWaldoProFreeRegister={isWaldoProFreeRegister}
                        startForFreeButtonClicked={startForFreeButtonClicked}
                      />
                    )}
                    {activeStep === PRO_REGISTRATION_STEP.PIN_VERIFICATION && (
                      <RegisterViewPinVerificationForm
                        values={values}
                        handleSubmit={handleSubmit}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        onEmailPinCodeRequest={onEmailPinCodeRequest}
                        errors={errors}
                        touched={touched}
                        loading={loading}
                        onGoBack={onGoBack}
                      />
                    )}
                    {activeStep === PRO_REGISTRATION_STEP.PASSWORD_CREATION && (
                      <RegisterViewPasswordCreateForm
                        values={values}
                        handleSubmit={handleSubmit}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        errors={errors}
                        touched={touched}
                        loading={loading}
                      />
                    )}

                    {activeStep === PRO_REGISTRATION_STEP.CART && (
                      <RegisterCheckoutCart
                        values={values}
                        handleSubmit={handleSubmit}
                        handleChange={handleChange}
                        photoFinderCreditItems={photoFinderCreditItems}
                        photoManagerSubscriptionItems={
                          photoManagerSubscriptionItems
                        }
                        submitting={loading}
                        accountPromoCode={accountPromoCode}
                        skipPhotoFinder={skipPhotoFinder}
                        skipFreeTrial={skipFreeTrial}
                        isWaldoProFreeRegister={isWaldoProFreeRegister}
                      />
                    )}
                    {activeStep ===
                      PRO_REGISTRATION_STEP.FUNDRAISER_MARKETING_COMMITMENT && (
                      <RegistrationFundraiserMarketingCommitment
                        values={values}
                        handleSubmit={handleSubmit}
                        handleChange={handleChange}
                        submitting={loading}
                        isValid={isValid}
                        onGoBack={onGoBack}
                        validateForm={validateForm}
                        addonForOnlinePurchasesEnabled={!noMarketing}
                      />
                    )}
                    {activeStep === PRO_REGISTRATION_STEP.PLIC_LOGIN && (
                      <div>
                        <p>
                          You are almost done!. To complete your registration,
                          please click the button below and login into your PLIC
                          Books account
                        </p>
                        <PLICBooksLogin state="/app">
                          Sign up with PLIC Books
                        </PLICBooksLogin>
                      </div>
                    )}
                  </Fragment>
                );
              }}
            />
          </div>
        </div>
        <Prompt
          when={activeStep === PRO_REGISTRATION_STEP.CART}
          message={() => {
            onPhotoManagerPurchaseReminderSet({
              sessionState: SESSION_STATE.END,
            });

            return false;
          }}
        />
      </MarcomLayout.Wrapper>
    </StripeElements>
  );
};

export default RegisterView;
